import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PointsLedger = ({ pointsData, onRedeemReward }) => {
  const [selectedReward, setSelectedReward] = useState(null);
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  const displayedTransactions = showAllTransactions 
    ? pointsData?.transactions 
    : pointsData?.transactions?.slice(0, 5);

  const handleRedeemReward = (reward) => {
    if (pointsData?.balance >= reward?.cost) {
      onRedeemReward(reward);
      setSelectedReward(null);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earned': return 'Plus';
      case 'redeemed': return 'Minus';
      case 'bonus': return 'Gift';
      default: return 'Circle';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'earned': return 'text-success';
      case 'redeemed': return 'text-error';
      case 'bonus': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success rounded-full flex items-center justify-center">
          <Icon name="Star" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Points & Rewards</h2>
          <p className="text-sm text-muted-foreground">Earn points and redeem rewards</p>
        </div>
      </div>
      {/* Points Balance */}
      <div className="bg-gradient-to-r from-success to-accent rounded-lg p-6 text-white mb-6">
        <div className="text-center">
          <p className="text-sm opacity-80 mb-2">Available Points</p>
          <p className="text-4xl font-bold mb-2">{pointsData?.balance?.toLocaleString()}</p>
          <p className="text-sm opacity-90">
            Lifetime Earned: {pointsData?.lifetimeEarned?.toLocaleString()} points
          </p>
        </div>
      </div>
      {/* Available Rewards */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Available Rewards</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pointsData?.availableRewards?.map((reward) => (
            <div
              key={reward?.id}
              className={`border rounded-lg p-4 transition-colors ${
                pointsData?.balance >= reward?.cost
                  ? 'border-success bg-success/5 hover:bg-success/10' :'border-border bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{reward?.title}</h4>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-foreground">
                    {reward?.cost?.toLocaleString()}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{reward?.description}</p>
              <Button
                variant={pointsData?.balance >= reward?.cost ? "default" : "outline"}
                size="sm"
                disabled={pointsData?.balance < reward?.cost}
                onClick={() => handleRedeemReward(reward)}
                className="w-full"
              >
                {pointsData?.balance >= reward?.cost ? 'Redeem' : 'Insufficient Points'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Transactions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground">Recent Activity</h3>
          {pointsData?.transactions?.length > 5 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllTransactions(!showAllTransactions)}
            >
              {showAllTransactions ? 'Show Less' : 'View All'}
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {displayedTransactions?.map((transaction) => (
            <div
              key={transaction?.id}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  transaction?.type === 'earned' ? 'bg-success/20' :
                  transaction?.type === 'redeemed' ? 'bg-error/20' : 'bg-accent/20'
                }`}>
                  <Icon
                    name={getTransactionIcon(transaction?.type)}
                    size={16}
                    className={getTransactionColor(transaction?.type)}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {transaction?.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(transaction.date)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${getTransactionColor(transaction?.type)}`}>
                  {transaction?.type === 'earned' || transaction?.type === 'bonus' ? '+' : '-'}
                  {transaction?.points?.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>

        {pointsData?.transactions?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Star" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No point activity yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Start earning points by purchasing tickets!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsLedger;